import React from 'react';
import { motion } from 'framer-motion';
import { Compass, Share2, PenTool, LineChart } from 'lucide-react';
import { InteriorSlide, SlideHeader } from './SlideShell';
import type { SlideProps } from './SlideShell';
export function Slide04Approach({
  index
}: SlideProps) {
  const pillars = [{
    icon: Compass,
    title: 'We build the campaign strategy',
    text: 'Based on your brand objectives and country data, we design the full campaign plan: what to say, to whom, on which channel.',
    delay: 0.4
  }, {
    icon: Share2,
    title: 'We run the distribution',
    text: 'QR codes at congresses and symposia, compliant WhatsApp channels, targeted digital placements.',
    delay: 0.55
  }, {
    icon: PenTool,
    title: 'We create the content',
    text: 'Snaks, quizzes, non-branded WhatsApp episodes, medical association banners — all built for mobile-first HCP audiences in Africa.',
    delay: 0.7
  }, {
    icon: LineChart,
    title: 'We optimize in real time',
    text: "Automated alerts when something isn't performing. Weekly reports. Data-driven decisions on what to push next.",
    delay: 0.85
  }];
  return <InteriorSlide surface data-id="element-89">
      <SlideHeader eyebrow="Our Model" data-id="element-90">What Snakker Does</SlideHeader>

      <div className="flex-1 flex flex-col lg:flex-row items-center gap-12 lg:gap-16 max-w-6xl mx-auto w-full" data-id="element-91">
        {/* Left: Video Visual */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.3,
        duration: 0.8
      }} className="flex-1 w-full relative aspect-[4/5] max-w-md rounded-3xl overflow-hidden shadow-2xl border-8 border-white bg-[var(--brand)]" data-id="element-92">
          <div className="absolute inset-0 bg-black/10 flex flex-col items-center justify-center text-center p-8" data-id="element-93">
            <p className="text-4xl md:text-5xl font-bold text-white tracking-tight leading-tight drop-shadow-md" data-id="element-94">
              We don't just build Snaks.
              <br data-id="element-95" />
              <br data-id="element-96" />
              We run campaigns.
            </p>
          </div>
        </motion.div>

        {/* Right: Pillars */}
        <div className="flex-1 space-y-6 w-full" data-id="element-97">
          <motion.p initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3,
          duration: 0.5
        }} className="text-[clamp(1.1rem,1.4vw,1.3rem)] text-[var(--muted)] leading-relaxed mb-8" data-id="element-98">
            We own the outcome. We tell you what's working and what to do next.
            Just like we do for GSK in Nigeria and Kenya.
          </motion.p>

          {pillars.map((pillar, i) => {
          const Icon = pillar.icon;
          return <motion.div key={pillar.title} initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: pillar.delay,
            duration: 0.5
          }} className="bg-white p-6 rounded-2xl shadow-sm border border-black/5 flex gap-6 items-center" data-id="element-99">
                <div className="h-14 w-14 rounded-full bg-[var(--surface)] flex items-center justify-center text-[var(--brand)] flex-shrink-0" data-id="element-100">
                  <Icon className="h-7 w-7" data-id="element-101" />
                </div>
                <div data-id="element-102">
                  <h3 className="text-lg font-bold text-[var(--fg)] mb-1" data-id="element-103">
                    {pillar.title}
                  </h3>
                  <p className="text-[var(--muted)] text-sm leading-relaxed" data-id="element-104">
                    {pillar.text}
                  </p>
                </div>
              </motion.div>;
        })}
        </div>
      </div>
    </InteriorSlide>;
}